import type { DistanceGapReasonCode, DistanceResolutionOutcome, ResolveDistanceGapInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, Radio } from 'antd';
import { OUTCOME_HINTS, OUTCOME_LABELS, outcomesFor } from '../policies/distance-labels.policy';

interface ResolveGapDrawerProps {
  open: boolean;
  reason: DistanceGapReasonCode | null;
  error: string | null;
  submitting: boolean;
  onResolve: (input: ResolveDistanceGapInput) => Promise<boolean>;
  onClose: () => void;
}

interface ResolveFormValues {
  outcome: DistanceResolutionOutcome;
  note: string;
}

/** How the gap was settled, and why: a short form, so a drawer. A later answer supersedes this one without erasing it. */
export function ResolveGapDrawer({ open, reason, error, submitting, onResolve, onClose }: ResolveGapDrawerProps) {
  const [form] = Form.useForm<ResolveFormValues>();

  async function submit(values: ResolveFormValues) {
    if (await onResolve(values)) {
      form.resetFields();
      onClose();
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Resolve distance gap" placement="right" size={420} destroyOnHidden>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<ResolveFormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="outcome" label="What happened" rules={[{ required: true, message: 'Choose an answer' }]}>
          <Radio.Group className="flex flex-col gap-3">
            {outcomesFor(reason).map((outcome) => (
              <Radio key={outcome} value={outcome} className="items-start">
                <span className="block font-medium">{OUTCOME_LABELS[outcome]}</span>
                <span className="block text-sm text-muted">{OUTCOME_HINTS[outcome]}</span>
              </Radio>
            ))}
          </Radio.Group>
        </Form.Item>
        <Form.Item name="note" label="Note" rules={[{ required: true, whitespace: true, message: 'Say why' }]}>
          <Input.TextArea rows={4} maxLength={1000} showCount />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Record answer
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
